import { useRef, useState } from 'react'
import { Link, Navigate, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useClickOutside } from '@/hooks/useClickOutside'
import { routes } from '@/routes'
import { MenuIcon, UserIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/Spinner'
import { Footer } from './Footer'
import { Logo } from './Logo'

/**
 * Moldura da área administrativa: cabeçalho enxuto com menu hambúrguer
 * (Perfil / Sair), sem busca nem tarja promocional.
 *
 * O acesso é barrado para quem não é admin. A checagem no cliente é só
 * conveniência de navegação — o backend precisa validar o papel em toda rota
 * `/admin/*`.
 */
export function AdminLayout() {
  const { isLoading, isAuthenticated, isAdmin } = useAuth()

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner size={28} className="text-brand" />
      </div>
    )
  }

  if (!isAuthenticated) return <Navigate to={routes.login} replace />
  if (!isAdmin) return <Navigate to={routes.home} replace />

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="sticky top-0 z-40 bg-brand">
        <div className="mx-auto flex h-14 max-w-[1200px] items-center justify-between px-4">
          <Logo />
          <AdminMenu />
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <Footer />
    </div>
  )
}

function AdminMenu() {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { logout } = useAuth()

  useClickOutside(containerRef, () => setOpen(false), open)

  async function handleLogout() {
    setOpen(false)
    await logout()
    navigate(routes.home)
  }

  const itemClass =
    'flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm transition-colors hover:bg-surface-muted'

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Menu do administrador"
        className="flex size-9 items-center justify-center rounded text-white transition-opacity hover:opacity-80"
      >
        <MenuIcon className="size-6" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+0.9rem)] z-50 w-44 overflow-hidden rounded bg-surface py-1 shadow-pop animate-[slide-down_0.18s_ease-out]"
        >
          <Link
            to={routes.profile}
            role="menuitem"
            onClick={() => setOpen(false)}
            className={`${itemClass} font-semibold text-brand`}
          >
            <UserIcon className="size-4" />
            Perfil
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className={`${itemClass} text-ink-faint`}
          >
            Sair
          </button>
        </div>
      )}
    </div>
  )
}
